import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link, useParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { useQueryClient } from 'react-query';
import { Button, Loader, Logo } from 'src/components';
import { useAlerts } from 'src/common/AlertManager';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { useUploadLinkQuery } from 'src/api/queries/uploadLinkQueries';
import { useCloseUploadLinkMutation } from 'src/api/mutations/uploadLinkMutations';
import { uploadFileToLink, UploadLinkFileError } from 'src/api/functions/uploadLinksApi';
import { queryNames } from 'src/api/utils/queryNames';
import { UploadLink as TUploadLink, UploadLinkAsset } from 'src/types/TUploadLink';
import { humanFileSize } from 'src/utils/transform';
import { acceptAttribute, assistantName, folderPath, remainingSlots, typeAllowed, typeNames } from './helpers';
import messages from './messages';
import styles from './styles.module.scss';

// The page an AI agent sends the user to when it can't read their disk
// (mcp#131, Path B). The link carries the agent's request (note, limits,
// collection); the backend applies the metadata and collection, so this page
// only picks files, uploads them and says when it's done.

const CONCURRENCY = 2;
const POLL_MS = 3000;

type ItemStatus = 'waiting' | 'uploading' | 'uploaded' | 'failed';

type Item = {
  key: string;
  // undefined for a file the link already had when this page opened
  file?: File;
  name: string;
  path?: string;
  status: ItemStatus;
  progress: number;
  assetId?: string;
  error?: string;
};

let itemCounter = 0;

export const UploadLink = () => {
  const { linkId } = useParams<{ linkId: string }>();
  const { formatMessage, formatRelativeTime, formatList } = useIntl();
  const { showAlert } = useAlerts();
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const [items, setItems] = useState<Item[]>([]);
  const [closedHere, setClosedHere] = useState(false);
  const aborts = useRef<Record<string, () => void>>({});
  const folderInput = useRef<HTMLInputElement>(null);

  const assetsById = useRef<Record<string, UploadLinkAsset>>({});
  const busy = items.some((i) => i.status === 'waiting' || i.status === 'uploading');
  // keep polling while any file sent through the link is still processing
  const isFinal = (status?: string) => status === 'ready' || status === 'failed';
  const processing =
    items.some((i) => i.status === 'uploaded' && !isFinal(assetsById.current[i.assetId!]?.status)) ||
    Object.values(assetsById.current).some((a) => !isFinal(a.status));

  const { data: link, error, isLoading, refetch } = useUploadLinkQuery(linkId, {
    refetchInterval: busy || processing ? POLL_MS : false,
  });
  const closeLink = useCloseUploadLinkMutation(linkId, {
    onSuccess: () => setClosedHere(true),
    onError: () => showAlert(formatMessage(messages.closeFailed)),
  });

  assetsById.current = useMemo(
    () => Object.fromEntries((link?.assets ?? []).map((a) => [a.asset_id, a])),
    [link?.assets],
  );

  // "Your AI assistant (Claude)" where the request is quoted; the plain
  // "your AI assistant" in running sentences
  const agent = assistantName(link?.app_name, link?.created_via);
  const assistantLabel = agent
    ? formatMessage(messages.yourAssistantNamed, { name: agent })
    : formatMessage(messages.yourAssistant);
  const assistantTitle = formatMessage(messages.yourAssistant);
  const assistantInline = formatMessage(messages.yourAssistantLower);

  // uploads the server hasn't counted yet
  const pendingHere = items.filter(
    (i) =>
      i.status === 'waiting' || i.status === 'uploading' || (i.status === 'uploaded' && !assetsById.current[i.assetId!]),
  ).length;
  const remaining = link ? remainingSlots(link.max_files, link.uploaded_count, pendingHere) : 0;

  const addFiles = useCallback(
    (files: File[]) => {
      if (!link || files.length === 0) return;
      const types = link.allowed_types;
      const allowed = files.filter((f) => {
        if (typeAllowed(f.type, types)) return true;
        showAlert(
          formatMessage(messages.rejectedType, { name: f.name, types: formatList(typeNames(types), { type: 'disjunction' }) }),
        );
        return false;
      });
      const accepted = allowed.slice(0, remaining);
      if (allowed.length > accepted.length) {
        showAlert(
          formatMessage(messages.rejectedFull, { count: allowed.length - accepted.length, max: link.max_files }),
        );
      }
      setItems((prev) => [
        ...prev,
        ...accepted.map((file) => ({
          key: `f${++itemCounter}`,
          file,
          name: file.name,
          path: folderPath(file),
          status: 'waiting' as ItemStatus,
          progress: 0,
        })),
      ]);
    },
    [link, remaining, showAlert, formatMessage, formatList],
  );

  const update = (key: string, patch: Partial<Item>) =>
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, ...patch } : i)));

  // Start waiting uploads, CONCURRENCY at a time.
  useEffect(() => {
    const running = items.filter((i) => i.status === 'uploading').length;
    const next = items.filter((i) => i.status === 'waiting').slice(0, Math.max(0, CONCURRENCY - running));
    next.forEach((item) => {
      if (!item.file) return;
      update(item.key, { status: 'uploading', progress: 0, error: undefined });
      const { promise, abort } = uploadFileToLink({
        linkId,
        file: item.file,
        path: item.path,
        accessToken,
        onProgress: (fraction) => update(item.key, { progress: fraction }),
      });
      aborts.current[item.key] = abort;
      promise
        .then((assetId) => {
          update(item.key, { status: 'uploaded', progress: 1, assetId });
          queryClient.invalidateQueries([queryNames.uploadLink, linkId]);
        })
        .catch((err: UploadLinkFileError) => {
          update(item.key, { status: 'failed', error: err?.message });
          // 403: the link stopped taking files (closed, expired, full); show why
          if (err?.status === 403) refetch();
        })
        .finally(() => delete aborts.current[item.key]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // Leaving mid-upload loses the files in flight.
  useEffect(() => {
    if (!busy) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = formatMessage(messages.leaveWarning);
      return event.returnValue;
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [busy, formatMessage]);

  useEffect(() => () => Object.values(aborts.current).forEach((abort) => abort()), []);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: addFiles,
    noClick: true,
    noKeyboard: true,
    disabled: !link || link.state !== 'open' || remaining === 0,
  });

  // A folder picker: webkitdirectory isn't in React's input typings.
  useEffect(() => {
    folderInput.current?.setAttribute('webkitdirectory', '');
  }, [link]);

  const onFolderPicked = (event: ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(event.target.files ?? []));
    event.target.value = '';
  };

  if (isLoading) {
    return (
      <Shell>
        <div className={styles.center}>
          <Loader size="large" />
        </div>
      </Shell>
    );
  }

  if (error || !link) {
    const notFound = (error as any)?.status === 404 || (error as any)?.status === 403;
    return (
      <Shell>
        <Message
          title={formatMessage(notFound ? messages.notFoundTitle : messages.loadFailed)}
          body={notFound ? formatMessage(messages.notFoundBody) : undefined}
          action={
            notFound ? undefined : (
              <Button mode="secondary" onClick={() => refetch()}>
                {formatMessage(messages.tryAgain)}
              </Button>
            )
          }
        />
      </Shell>
    );
  }

  if (link.state === 'closed') {
    const stillProcessing = (link.assets ?? []).some((a) => !['ready', 'failed'].includes(a.status));
    return (
      <Shell>
        {closedHere ? (
          <Message
            tone="success"
            title={formatMessage(messages.closedTitle)}
            body={
              <>
                {formatMessage(messages.closedBody, { count: link.uploaded_count, assistant: assistantInline })}
                {stillProcessing && link.uploaded_count > 0 && (
                  <span className={styles.subtle}>
                    {' '}
                    {formatMessage(messages.closedStillProcessing, { assistant: assistantTitle })}
                  </span>
                )}
              </>
            }
            action={
              <Link to="/assets" className={styles.textLink}>
                {formatMessage(messages.viewAssets)}
              </Link>
            }
          />
        ) : (
          <Message
            title={formatMessage(messages.alreadyClosedTitle)}
            body={formatMessage(messages.alreadyClosedBody, { assistant: assistantInline })}
          />
        )}
      </Shell>
    );
  }

  if (link.state === 'expired' && !busy) {
    return (
      <Shell>
        <Message
          title={formatMessage(messages.expiredTitle)}
          body={formatMessage(messages.expiredBody, { assistant: assistantInline })}
        />
      </Shell>
    );
  }

  // Files the link already had (uploaded before a reload, or in another tab)
  // are listed too, so the page always shows everything sent through it.
  const mine = new Set(items.map((i) => i.assetId).filter(Boolean));
  const earlier: Item[] = (link.assets ?? [])
    .filter((a) => !mine.has(a.asset_id))
    .map((a) => ({ key: a.asset_id, name: a.filename, status: 'uploaded', progress: 1, assetId: a.asset_id }));
  const shown = [...earlier, ...items].map((i) => ({
    item: i,
    asset: i.assetId ? assetsById.current[i.assetId] : undefined,
  }));
  const readyCount = shown.filter(({ asset }) => asset?.status === 'ready').length;
  const failedCount = shown.filter(({ item, asset }) => item.status === 'failed' || asset?.status === 'failed').length;
  const types = link.allowed_types;

  return (
    <Shell>
      <section className={styles.request}>
        <p className={styles.eyebrow}>{formatMessage(messages.askedBy, { assistant: assistantLabel })}</p>
        {link.note && <h1 className={styles.note}>{link.note}</h1>}
        {!link.note && <h1 className={styles.note}>{formatMessage(messages.pageTitle)}</h1>}
        <ul className={styles.terms}>
          <li>{formatMessage(messages.limitFiles, { remaining })}</li>
          {types && types.length > 0 && <li>{formatMessage(messages.limitTypes, { types: formatList(typeNames(types), { type: 'disjunction' }) })}</li>}
          {link.collection_id && <li>{formatMessage(messages.limitCollection)}</li>}
          <li>{formatMessage(messages.limitExpires, { time: relativeExpiry(link, formatRelativeTime) })}</li>
        </ul>
        <p className={styles.privacy}>{formatMessage(messages.privacy, { assistant: assistantTitle })}</p>
      </section>

      <div
        {...getRootProps({
          className: [styles.dropzone, isDragActive ? styles.dropzoneActive : '', remaining === 0 ? styles.dropzoneOff : '']
            .filter(Boolean)
            .join(' '),
        })}
        data-test="upload-link-dropzone"
      >
        <input {...getInputProps({ accept: acceptAttribute(types) })} />
        <input
          ref={folderInput}
          type="file"
          multiple
          className={styles.hidden}
          onChange={onFolderPicked}
          data-test="upload-link-folder-input"
        />
        <p className={`${styles.dropTitle} ${styles.pointerOnly}`}>
          {formatMessage(isDragActive ? messages.dropActive : messages.dropHere)}
        </p>
        <div className={styles.pickers}>
          <Button mode="primary" onClick={open} disabled={remaining === 0}>
            {formatMessage(messages.chooseFiles)}
          </Button>
          {/* no dragging and no folder picker on touch devices (iOS Safari) */}
          <span className={`${styles.or} ${styles.pointerOnly}`}>{formatMessage(messages.or)}</span>
          <span className={styles.pointerOnly}>
            <Button mode="tertiary" onClick={() => folderInput.current?.click()} disabled={remaining === 0}>
              {formatMessage(messages.chooseFolder)}
            </Button>
          </span>
        </div>
        <p className={`${styles.subtle} ${styles.pointerOnly}`}>{formatMessage(messages.folderNote)}</p>
      </div>

      {shown.length > 0 && (
        <section className={styles.files} aria-live="polite">
          <p className={styles.summary}>
            {formatMessage(messages.summary, { ready: readyCount, total: shown.length, failed: failedCount })}
          </p>
          <ul className={styles.fileList}>
            {shown.map(({ item, asset }) => (
              <FileRow
                key={item.key}
                item={item}
                asset={asset}
                onRetry={() => update(item.key, { status: 'waiting', progress: 0 })}
                onRemove={() => setItems((prev) => prev.filter((i) => i.key !== item.key))}
              />
            ))}
          </ul>
        </section>
      )}

      <footer className={styles.footer}>
        <p className={styles.subtle}>
          {busy ? formatMessage(messages.doneWaiting) : formatMessage(messages.doneHint, { assistant: assistantTitle })}
        </p>
        <Button
          mode="primary"
          onClick={() => closeLink.mutate()}
          loading={closeLink.isLoading}
          disabled={busy}
          datatest="upload-link-done"
        >
          {formatMessage(link.uploaded_count === 0 && shown.length === 0 ? messages.closeWithout : messages.done)}
        </Button>
      </footer>
    </Shell>
  );
};

function relativeExpiry(link: TUploadLink, formatRelativeTime: (v: number, u: Intl.RelativeTimeFormatUnit) => string) {
  const minutes = Math.max(1, Math.round((Date.parse(link.expires_at) - Date.now()) / 60000));
  return minutes < 90 ? formatRelativeTime(minutes, 'minute') : formatRelativeTime(Math.round(minutes / 60), 'hour');
}

const Shell = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.page}>
    <div className={styles.column}>
      <div className={styles.logo}>
        <Logo linkable={false} />
      </div>
      <main className={styles.card}>{children}</main>
    </div>
  </div>
);

const Message = ({
  title,
  body,
  action,
  tone,
}: {
  title: string;
  body?: React.ReactNode;
  action?: React.ReactNode;
  tone?: 'success';
}) => (
  <div className={styles.message} data-test="upload-link-message">
    {tone === 'success' && <span className={styles.check} aria-hidden="true" />}
    <h1 className={styles.note}>{title}</h1>
    {body && <p className={styles.messageBody}>{body}</p>}
    {action && <div className={styles.messageAction}>{action}</div>}
  </div>
);

const FileRow = ({
  item,
  asset,
  onRetry,
  onRemove,
}: {
  item: Item;
  asset?: UploadLinkAsset;
  onRetry: () => void;
  onRemove: () => void;
}) => {
  const { formatMessage } = useIntl();
  let state: 'waiting' | 'uploading' | 'processing' | 'ready' | 'failed' = 'waiting';
  let label = formatMessage(messages.statusWaiting);
  let detail: string | undefined;
  if (item.status === 'uploading') {
    state = 'uploading';
    label = formatMessage(messages.statusUploading, { percent: Math.floor(item.progress * 100) });
  } else if (item.status === 'failed') {
    state = 'failed';
    label = formatMessage(messages.statusFailed);
    detail = item.error;
  } else if (item.status === 'uploaded') {
    if (asset?.status === 'ready') {
      state = 'ready';
      label = formatMessage(messages.statusReady);
    } else if (asset?.status === 'failed') {
      state = 'failed';
      label = formatMessage(messages.statusFailed);
      detail = asset.reason;
    } else {
      state = 'processing';
      label = formatMessage(messages.statusProcessing);
    }
  }
  const progress = state === 'uploading' ? item.progress : state === 'waiting' ? 0 : 1;

  return (
    <li className={`${styles.fileRow} ${styles[state]}`} data-test="upload-link-file">
      <div className={styles.fileMain}>
        <span className={styles.fileName} title={item.path ?? item.name}>
          {item.path ?? item.name}
        </span>
        {item.file && <span className={styles.fileSize}>{humanFileSize(item.file.size)}</span>}
      </div>
      <div
        className={styles.bar}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(progress * 100)}
      >
        <span className={styles.barFill} style={{ width: `${progress * 100}%` }} />
      </div>
      <div className={styles.fileStatus}>
        <span className={styles.statusLabel}>{label}</span>
        {detail && <span className={styles.statusDetail}>{detail}</span>}
        {item.status === 'failed' && (
          <span className={styles.rowActions}>
            <button type="button" className={styles.textButton} onClick={onRetry}>
              {formatMessage(messages.retry)}
            </button>
            <button type="button" className={styles.textButton} onClick={onRemove}>
              {formatMessage(messages.remove)}
            </button>
          </span>
        )}
      </div>
    </li>
  );
};
